import { Modal as M, Image, Skeleton } from "antd";
import { down } from "styled-breakpoints";
import styled from "styled-components";
import { useEffect, useState, SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";
import { checkUnknownError } from "src/utils/errors";
import { logError } from "src/utils/logger";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import Typography from "@sunday/component/lib/components/Typography";
import { color } from "../../styles/globals";
import { getConsentsByCode } from "../../api/consent";
import Button from "../Common/Button";

const { Title: SundayV3Title, Body: SundayV3Body } = Typography;

const Modal = styled(M)<{ footer?: JSX.Element }>`
  max-width: 100vw;
  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
  }
  .ant-modal-header {
  }
  .ant-modal-close {
    margin-top: 10px;
  }
  .ant-modal-body {
    padding: 20px 16px;
    height: 70vh;
    background-color: ${color.charcoal01};
    overflow: auto;
  }
  .ant-modal-footer {
    display: ${(props) => (props.footer ? "block" : "none")};
    width: 100%;
    background-color: ${color.white};
  }
  ${down("lg")} {
    top: 0;
    margin: 0px;
    padding: 0px;
    width: 100% !important;
    .ant-modal,
    .ant-modal-content {
      border-radius: 0px !important;
      height: 100vh;
      width: 100vw;
      margin: 0;
      top: 0;
    }
    .ant-modal-body {
      overflow: auto;
      height: calc(100vh - 141px);
    }
    .ant-modal-footer {
      padding: 16px;
      position: fixed;
      bottom: 0;
      width: 100%;
      background: white;
    }
  }
`;

const CloseIcon = styled(Image)`
  cursor: pointer;
`;

const ModalBody = styled(SundayV3Body).attrs({
  designLevel: 2,
  weight: "regular",
})`
  &&& {
    color: ${color.charcoal09};
    line-height: 1.5;
  }
`;

const Title = styled(SundayV3Title).attrs({
  designLevel: 2,
  weight: "bold",
})`
  &&& {
    color: ${color.charcoal10};
    line-height: 1.5;
  }
`;

export type ConsentModalProp = {
  title: string;
  code: string;
  visible: boolean;
  onCloseModal?: (event?: SyntheticEvent<HTMLElement>) => void;
  lang: string;
  footer?: JSX.Element;
};

const ConsentModal = ({
  title = "",
  code = "",
  visible = false,
  onCloseModal = () => {},
  lang = "en",
  footer = null,
}: ConsentModalProp) => {
  const { t } = useTranslation("application-page");
  const [description, setDescription] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingError, setIsLoadingError] = useState<boolean>(false);
  const { lg } = useBreakpoint();

  const handleClose = (event?: SyntheticEvent<HTMLElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    event?.nativeEvent.stopImmediatePropagation?.();
    onCloseModal(event);
  };

  const onDisplayConsent = async () => {
    setIsLoading(true);
    setIsLoadingError(false);
    try {
      setDescription("");
      const consent = await getConsentsByCode(code, lang);
      if (!consent) {
        setIsLoadingError(true);
      }
      setIsLoadingError(false);
      setDescription(consent.detail);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsLoadingError(true);
      const isUnknowError = checkUnknownError(error);
      if (isUnknowError) {
        logError(
          "[consent-modal-component] display consent fail",
          { code, lang },
          error
        );
      }
    }
  };

  useEffect(() => {
    if (visible) {
      onDisplayConsent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, code, lang]);

  const styles = {
    modalTitle: {
      minHeight: "24px",
      margin: "0px 56px",
    },
    noDataLogo: {
      height: "144px",
      marginBottom: "17px",
    },
    errorBox: {
      width: "255px",
      margin: "28px auto",
    },
    errorMessage: {
      marginBottom: "17px",
      margin: "0px auto 17px auto",
    },
    errorMessageTitle: {
      marginButton: "8px",
    },
    refreshButton: {
      width: "172px",
    },
  };

  return (
    <Modal
      centered={lg}
      title={
        <Title style={{ textAlign: "center", ...styles.modalTitle }}>
          {title}
        </Title>
      }
      footer={footer}
      visible={visible}
      onCancel={handleClose}
      destroyOnClose
      closeIcon={
        <CloseIcon
          preview={false}
          src="/static/pages/planPage/x-icon.svg"
          alt="close-icon"
        />
      }
    >
      <Skeleton
        loading={isLoading}
        active
        title={false}
        paragraph={{ rows: 15, width: "100%" }}
      >
        <ModalBody>{description}</ModalBody>
      </Skeleton>
      {isLoadingError && (
        <div style={{ textAlign: "center", ...styles.errorBox }}>
          <Image
            src="/static/pages/applicationPage/consent-no-data-icon.svg"
            preview={false}
            style={styles.noDataLogo}
            alt="consent-no-data-icon"
          />
          <div style={{ textAlign: "center", ...styles.errorMessage }}>
            <Title>{t("agreementForm.errorMessage.loadingError.title")}</Title>
            <ModalBody>
              {t("agreementForm.errorMessage.loadingError.description")}
            </ModalBody>
          </div>
          <Button
            type="outlined"
            onClick={() => onDisplayConsent()}
            style={styles.refreshButton}
          >
            {t("agreementForm.errorMessage.loadingError.refreshButton")}
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default ConsentModal;
